import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  PropsWithChildren,
} from 'react';
import { NotificationsContext } from '../context/NotificationsContext';
import { UserDataContext } from '../context/UserDataContext';
import { supabase } from '../supabaseClient';
import { useSupabaseListener } from '../hooks/useSupabaseListener';
import { NotificationData } from '../model/types';

export type { NotificationData as Notification };

const POLL_INTERVAL_MS = 30_000; // 30-second fallback poll

export const NotificationsProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { userRecord } = useContext(UserDataContext);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);
  const [polledNotifications, setPolledNotifications] = useState<NotificationData[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchNotifications = useCallback(async (): Promise<NotificationData[]> => {
    if (!userRecord) return [];
    const { data, error: fetchError } = await supabase
      .from('notifications')
      .select('*')
      .eq('user_record_id', userRecord.id)
      .order('created_at', { ascending: false });
    if (fetchError) {
      console.error('NotificationsProvider fetch error:', fetchError.message);
      return [];
    }
    return (data ?? []) as NotificationData[];
  }, [userRecord]);

  const { data: realtimeNotifications, isLoading } = useSupabaseListener<NotificationData>(
    {
      table: 'notifications',
      filter: userRecord ? `user_record_id=eq.${userRecord.id}` : undefined,
      fetchInitial: fetchNotifications,
      disabled: !userRecord,
    },
    [userRecord?.id],
  );

  // Polling fallback: re-fetch every 30s in case Realtime misses an event
  useEffect(() => {
    if (!userRecord) return;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const rows = await fetchNotifications();
      setPolledNotifications(rows);
    }, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [userRecord?.id, fetchNotifications]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge Realtime + polled results, deduplicated by id, newest-first
  const notifications = React.useMemo(() => {
    const map = new Map<string, NotificationData>();
    [...realtimeNotifications, ...polledNotifications].forEach((n) => {
      if (!map.has(n.id) || n.read !== map.get(n.id)!.read) {
        map.set(n.id, n);
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.created_at > b.created_at ? -1 : a.created_at < b.created_at ? 1 : 0,
    );
  }, [realtimeNotifications, polledNotifications]);

  const getNotifications = useCallback(() => notifications, [notifications]);

  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        getNotifications,
        isLoading,
        error,
        clearError,
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
